// Meeting detail + audit view (design decision #7). The AI's best-guess notes open
// as an editable markdown document; saving marks the meeting audited and re-syncs
// the brain thought from the edited text. The original AI draft is preserved and
// viewable. "Draft call report" generates the on-demand house-style HTML.
//
// ponytail: notes are edited as ONE markdown textarea (not per-section widgets with
// per-action tick/reject). It fully satisfies "easy to add or overwrite what the AI
// said"; upgrade to structured per-action editing only if the flat editor proves
// fiddly in real use.

import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Button, Icon, Badge } from "../../design/ui";
import { getMeeting, auditMeeting, draftMeetingReport, type MeetingDetail } from "../../lib/brain-api";

const STATUS_TONE: Record<string, "info" | "success" | "warning" | "danger" | "neutral"> = {
  recording: "warning",
  uploading: "warning",
  transcribing: "info",
  synthesizing: "info",
  ready: "warning",
  audited: "success",
  error: "danger",
};

export function MeetingAudit({
  customerId,
  visitId,
  meetingId,
  onClose,
  onSaved,
}: {
  customerId: string;
  visitId: string;
  meetingId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [detail, setDetail] = useState<MeetingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const [showAiDraft, setShowAiDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    let alive = true;
    getMeeting(customerId, visitId, meetingId)
      .then((d) => {
        if (!alive) return;
        setDetail(d);
        setNotes(d.notes || d.aiDraft || "");
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : "Failed to load meeting"));
    return () => {
      alive = false;
    };
  }, [customerId, visitId, meetingId]);

  async function save() {
    if (!notes.trim()) {
      enqueueSnackbar("Notes are empty", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      await auditMeeting({ customerId, visitId, meetingId, notesMarkdown: notes });
      enqueueSnackbar("Saved — meeting marked audited", { variant: "success" });
      onSaved();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to save", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function report() {
    setReporting(true);
    try {
      const { url } = await draftMeetingReport({ customerId, visitId, meetingId });
      window.open(url, "_blank", "noopener");
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to draft report", { variant: "error" });
    } finally {
      setReporting(false);
    }
  }

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Button size="sm" icon="arrow-left" onClick={onClose}>Back</Button>
        <p style={{ color: "var(--kt-danger, #b3261e)", marginTop: 12 }}>{error}</p>
      </div>
    );
  }
  if (!detail) return <div style={{ padding: 16, color: "var(--kt-text-muted)" }}>Loading meeting…</div>;

  const m = detail.meeting;
  const processing = ["transcribing", "synthesizing", "uploading", "recording"].includes(m.status);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Button size="sm" icon="arrow-left" onClick={onClose}>Back</Button>
        <strong style={{ fontSize: 15 }}>{m.title}</strong>
        <span style={{ fontSize: 12, color: "var(--kt-text-muted)" }}>{m.date}</span>
        <Badge tone={STATUS_TONE[m.status] ?? "neutral"}>{m.status}</Badge>
        <Badge tone="neutral" icon={m.source === "onsite" ? "microphone" : "video"}>
          {m.source === "onsite" ? "on-site" : "Meet"}
        </Badge>
      </div>

      {m.attendees.length > 0 && (
        <div style={{ fontSize: 12, color: "var(--kt-text-muted)" }}>
          Attendees: {m.attendees.join(", ")}
        </div>
      )}

      {m.status === "error" && (
        <div style={{ fontSize: 13, color: "var(--kt-danger, #b3261e)" }}>
          Pipeline error: {m.error || "unknown"}. Re-run by re-finalizing, or check the worker logs.
        </div>
      )}

      {processing ? (
        <div style={{ padding: 16, color: "var(--kt-text-muted)" }}>
          <Icon name="loader-2" size={15} /> Processing ({m.status})… notes will appear here when ready.
        </div>
      ) : (
        <>
          <label className="kt-field">
            <span className="kt-field-label">Notes (edit freely — this becomes the record)</span>
            <textarea
              className="kt-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={20}
              style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 13, lineHeight: 1.5 }}
            />
          </label>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant="primary" icon="check" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save & mark audited"}
            </Button>
            <Button icon="file-text" onClick={report} disabled={reporting}>
              {reporting ? "Generating…" : "Draft call report"}
            </Button>
          </div>
        </>
      )}

      {detail.aiDraft && detail.aiDraft !== notes && (
        <div>
          <button className="ab-pill" onClick={() => setShowAiDraft((v) => !v)}>
            <Icon name="robot" size={13} /> {showAiDraft ? "Hide" : "View"} original AI draft
          </button>
          {showAiDraft && (
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, background: "var(--kt-surface-2, #f6f7f8)", padding: 12, borderRadius: 8, marginTop: 8 }}>
              {detail.aiDraft}
            </pre>
          )}
        </div>
      )}

      {detail.transcript && (
        <div>
          <button className="ab-pill" onClick={() => setShowTranscript((v) => !v)}>
            <Icon name="file-description" size={13} /> {showTranscript ? "Hide" : "View"} transcript
          </button>
          {showTranscript && (
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, background: "var(--kt-surface-2, #f6f7f8)", padding: 12, borderRadius: 8, marginTop: 8, maxHeight: 400, overflow: "auto" }}>
              {detail.transcript}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
